import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Table,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';

const DataUpload = () => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [siteData, setSiteData] = useState([]);
  const [rejectedData, setRejectedData] = useState([]);
  const [skippedFileURL, setSkippedFileURL] = useState('');
  const [showResultsPanel, setShowResultsPanel] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [mandatoryModalOpen, setMandatoryModalOpen] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const allFields = [
    'State', 'Zone', 'Media type', 'Area', 'Location', 'Type', 'Unit',
    'Width', 'Height', 'total sqr ft', 'Lat', 'long', 'Vendor',
  ];
  const [mandatoryFields, setMandatoryFields] = useState([]);

  const toggleModal = () => setModalOpen(!modalOpen);
  const toggleMandatoryModal = () => setMandatoryModalOpen(!mandatoryModalOpen);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError('');
    } else {
      setFile(null);
      setFileName('');
      setError('Only CSV files are allowed. Please select a valid file.');
    }
  };

  const fetchAcceptedSites = async () => {
    try {
      const response = await fetch('https://acme.warburttons.com/api/uploadsites');
      if (!response.ok) throw new Error('Failed to fetch site data');
      const data = await response.json();
      setSiteData(data.data || []);
    } catch (err) {
      console.error('Failed to fetch site data:', err);
      setError('Could not retrieve existing site data.');
    }
  };

  useEffect(() => {
    fetchAcceptedSites();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please upload a CSV file.');
      return;
    }
    
    // Reset state for new submission
    setLoading(true);
    setError('');
    setSuccessMessage('');
    setAcceptedCount(0);
    setRejectedCount(0);
    setRejectedData([]);
    setSkippedFileURL('');
    setShowResultsPanel(false);

    const formData = new FormData();
    formData.append('csv_file', file);

    const fieldsToSend = mandatoryFields.length > 0 ? mandatoryFields : allFields;
    fieldsToSend.forEach((field) => {
      formData.append('mandatory_fields[]', field);
    });
    
    // ✅ LINTING FIX: Replaced the 'for...of' loop with '.forEach'
    console.log("Submitting the following data to the API:");
    formData.forEach((value, key) => {
      if (value instanceof File) {
        console.log(`${key}:`, { name: value.name, type: value.type, size: value.size });
      } else {
        console.log(`${key}:`, value);
      }
    });

    try {
      const response = await fetch('https://acme.warburttons.com/api/uploadsites', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();

      if (response.ok && result.success) {
        setAcceptedCount(result.success_count || 0);
        setRejectedCount(result.skipped_count || 0);
        setRejectedData(result.skipped_rows || []);
        setSkippedFileURL(result.skipped_file || '');
        setSuccessMessage('✅ Data submitted successfully!');
        setShowResultsPanel(true);
        setCurrentPage(1);
        fetchAcceptedSites();
      } else {
        setError(result.message || 'Upload failed. Please check the file and try again.');
      }
    } catch (err) {
      setError('A network error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseResults = () => {
    setShowResultsPanel(false);
  };

  const downloadRejectedData = () => {
    if (skippedFileURL) {
      window.open(skippedFileURL, '_blank');
      return;
    }

    if (!rejectedData.length) {
        alert("No rejected data to download.");
        return;
    }
    
    const headers = allFields.slice(0, rejectedData[0]?.length || 0);
    const csvContent = [
      headers.join(','),
      ...rejectedData.map(row =>
        row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'rejected_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAcceptedDataCSV = () => {
    if (!siteData.length) return;
    const headers = [
      'Code', 'State', 'Zone', 'Media Type', 'Area', 'Location', 'Type', 'Unit',
      'Width', 'Height', 'Total Sq Ft', 'Latitude', 'Longitude', 'Vendor',
    ];
    const rows = siteData.map((item) => [
      item.site_code, item.state, item.zone, item.media_type, item.area, item.location,
      item.type, item.unit, item.width, item.height, item.total_sqr_ft, item.lat,
      item.long, item.vendor,
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell ?? ''}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'accepted_data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(siteData.length / itemsPerPage);
  const paginatedData = siteData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <Card>
      <CardBody>
        {showResultsPanel && (
          <div className="mb-4 border p-3 bg-light position-relative">
            <Button close onClick={handleCloseResults} aria-label="Close" />
            {successMessage && <Alert color="success" className="mb-2">{successMessage}</Alert>}
            {acceptedCount > 0 && <Alert color="info" className="mb-2">✅ Accepted Rows: {acceptedCount}</Alert>}
            {rejectedCount > 0 && <Alert color="warning" className="mb-2">❌ Rejected Rows: {rejectedCount}</Alert>}
            
            {rejectedData.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <Button color="primary" onClick={toggleModal}>
                        👁 View Rejected Data
                    </Button>
                    <Button color="outline-danger" onClick={downloadRejectedData}>
                        📥 Download Rejected CSV
                    </Button>
                </div>
            )}
          </div>
        )}

        <div className="mb-4 d-flex gap-2 flex-wrap">
          <Button color="primary" onClick={toggleMandatoryModal}>
            🎯 Choose Mandatory Fields
          </Button>
          {/* <Button color="info">👁 View Demo</Button> */}
          <Button color="success" onClick={downloadAcceptedDataCSV}>
            📥 Download Accepted Data
          </Button>
        </div>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="csvFile" className="fw-bold">
              CSV File <span className="text-danger">*</span>
            </Label>
            <Input id="csvFile" type="file" accept=".csv" onChange={handleFileChange} />
            {fileName && <div className="mt-2 text-muted fst-italic">Selected file: {fileName}</div>}
          </FormGroup>
          <Button color="primary" className="mt-2" disabled={loading || !file}>
            {loading ? <><Spinner size="sm" /> Submitting...</> : 'SUBMIT'}
          </Button>
        </Form>

        {error && (
          <Alert color="danger" className="mt-3">
            {error}
          </Alert>
        )}

        <h5 className="mt-4">✅ Accepted Site Data</h5>
        <Table bordered responsive className="mt-2">
          <thead>
            <tr>
              <th>Code</th> <th>State</th> <th>Zone</th> <th>Media Type</th> <th>Area</th> <th>Location</th>
              <th>Type</th> <th>Unit</th> <th>Width</th> <th>Height</th> <th>Total Sq Ft</th>
              <th>Latitude</th> <th>Longitude</th> <th>Vendor</th>
            </tr>
          </thead>
          <tbody>
            {loading && !paginatedData.length ? (
              <tr><td colSpan="14" className="text-center"><Spinner /> Loading Data...</td></tr>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((item) => (
                <tr key={item.site_code || `${item.lat}-${item.long}-${item.vendor}`}>
                  <td>{item.site_code}</td> <td>{item.state}</td> <td>{item.zone}</td>
                  <td>{item.media_type}</td> <td>{item.area}</td> <td>{item.location}</td>
                  <td>{item.type}</td> <td>{item.unit}</td> <td>{item.width}</td>
                  <td>{item.height}</td> <td>{item.total_sqr_ft}</td> <td>{item.lat}</td>
                  <td>{item.long}</td> <td>{item.vendor}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="14" className="text-center">No accepted data available.</td>
              </tr>
            )}
          </tbody>
        </Table>

        {siteData.length > itemsPerPage && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button color="secondary" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
              Previous
            </Button>
            <span>Page {currentPage} of {totalPages}</span>
            <Button color="secondary" onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
              Next
            </Button>
          </div>
        )}

        <Modal isOpen={mandatoryModalOpen} toggle={toggleMandatoryModal}>
          <ModalHeader toggle={toggleMandatoryModal}>Select Mandatory Fields for Upload</ModalHeader>
          <ModalBody>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={mandatoryFields.length === allFields.length}
                  onChange={(e) => setMandatoryFields(e.target.checked ? allFields : [])}
                />{' '}
                <strong>Select All</strong>
              </Label>
            </FormGroup>
            <hr />
            {allFields.map((field) => (
              <FormGroup check key={field}>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={mandatoryFields.includes(field)}
                    onChange={() => {
                      setMandatoryFields(
                        mandatoryFields.includes(field)
                          ? mandatoryFields.filter((f) => f !== field)
                          : [...mandatoryFields, field]
                      );
                    }}
                  /> {field}
                </Label>
              </FormGroup>
            ))}
            <div className="d-flex justify-content-end mt-3">
              <Button color="warning" className="me-2" onClick={toggleMandatoryModal}>APPLY</Button>
              <Button color="secondary" onClick={() => { setMandatoryFields([]); toggleMandatoryModal(); }}>CANCEL</Button>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={modalOpen} toggle={toggleModal} size="xl" scrollable>
          <ModalHeader toggle={toggleModal}>❌ Rejected Records ({rejectedData.length})</ModalHeader>
          <ModalBody>
            <Table bordered responsive>
              <thead>
                <tr>
                   {rejectedData[0]?.map((_, index) => (
                        <th key={`header-${allFields[index] || index}`}>{allFields[index] || `Column ${index + 1}`}</th>
                   ))}
                </tr>
              </thead>
              <tbody>
                {rejectedData.map((row, ) => (
                  <tr >
                    {row.map((cell, ) => (
                      <td >{cell ?? '-'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </ModalBody>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default DataUpload;